import { apiErrorMessage } from '@agos/api-client'
import {
  getBackupsFilenameDownload,
  getGetBackupsQueryKey,
  useGetBackups,
  useGetBackupsLastRun,
  usePostBackupsTrigger,
} from '@agos/api-client/devices'
import {
  T,
  downloadBlobFile,
  formatCount,
  formatDateTime,
  scheduledJobsOffNote,
  tt,
  useAuth,
} from '@agos/shared'
import { EmptyState, PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Clock, Database, Download, HardDrive, Info, RefreshCw } from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  backupsOf,
  databaseOf,
  formatSize,
  isServableFilename,
  lastRunMessage,
  lastRunOf,
  listMessage,
  listState,
  runState,
  restoreInstructions,
  storageNote,
  totalSize,
  type BackupFile,
} from './backup-list'

/**
 * `/settings-backups` — the database copies on disk.
 *
 * **FB-151.** The paragraph at the top described a system that is gone. *Zálohy sú uložené v cloudovom
 * úložisku* — `BackupService` writes to a mounted volume, and says why: *"Legacy pushes these into Replit's
 * bucket; Replit is gone from this system."* And *posledných 24 záloh* is a count per **database**: the
 * hourly job runs per franchise **plus central**, each keeping its own 24. An empty list, meanwhile, reads as
 * *backups are broken* when it may equally be a job that has not run yet — and the page offered no way to
 * tell, though every run writes to `system_logs`.
 *
 * **FB-152.** `formatBytes` wrote `4.6 MB` on a Slovak page — a decimal point, the `toFixed` family again —
 * and divided by 1 024 while labelling KB. And a failed download was a `window.alert` with an untranslated
 * literal, on a POS tablet.
 *
 * Ported from legacy `pages/settings-backups.tsx` (217 lines).
 */

export function SettingsBackupsPage() {
  /* FB-812, §9.532 - **two reads, three claims, and `isError` was consulted on neither.** `isLoading` gates
     both a spinner and the run banner, so loading was handled and failure was not. A failed load told the
     operator *no backup on disk*, *the job has never run once - there is no trace of it in the system log*,
     and *either it has not run yet or it is not running; every attempt is logged, that is where you tell the
     difference* - the last two being **evidential**, and the third an instruction to go and investigate two
     states neither of which is the real one. */
  const query = useGetBackups()
  const lastRunQuery = useGetBackupsLastRun()
  const backups = backupsOf(query.data)

  const { toast } = useToast()
  const { user, hasPermission } = useAuth()
  const queryClient = useQueryClient()
  const [downloading, setDownloading] = useState<string | null>(null)

  // `/backups/*` is one of the four sections an `admin` may write as well as read.
  const writable = mayWrite('backups', user?.systemRole, hasPermission)
  const readOnly = readOnlyReason('backups', user?.systemRole, hasPermission)

  const trigger = usePostBackupsTrigger({
    mutation: {
      onSuccess: (result) => {
        void queryClient.invalidateQueries({ queryKey: getGetBackupsQueryKey() })
        const filename = (result as { filename?: unknown } | undefined)?.filename
        toast({
          title: tt('Záloha vytvorená'),
          description: typeof filename === 'string' ? filename : undefined,
        })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Zálohovanie zlyhalo'),
          description: apiErrorMessage(
            caught,
            tt('Pozrite systémový log - každý pokus sa doň zapisuje.'),
          ),
        }),
    },
  })

  const download = async (backup: BackupFile) => {
    setDownloading(backup.filename)
    try {
      // Through the generated client (§6), asking for a blob: the spec types the response as a string
      // because springdoc describes a gzip as one, and decoding a dump as text would corrupt it.
      const bytes = (await getBackupsFilenameDownload(backup.filename, {
        responseType: 'blob',
      })) as unknown as Blob
      downloadBlobFile(backup.filename, bytes)
    } catch {
      // Legacy used `window.alert`, untranslated, on a tablet.
      toast({
        variant: 'destructive',
        title: tt('Stiahnutie sa nepodarilo'),
        description: tt('Súbor sa nedal načítať zo servera.'),
      })
    } finally {
      setDownloading(null)
    }
  }

  const state = listState(backups)
  // FB-151. The file list alone cannot tell "has not run yet" from "stopped working three weeks ago",
  // and the second is the one that matters on the day somebody needs a restore.
  const lastRun = lastRunOf(lastRunQuery.data)
  const jobsOff = scheduledJobsOffNote(lastRun.scheduledJobsEnabled)
  const ran = runState(lastRun)

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Zálohy databázy')}
        description={tt('Čo je na disku a ako sa z toho obnovuje')}
        icon={<Database className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-refresh
              disabled={query.isFetching}
              onClick={() =>
                void queryClient.invalidateQueries({ queryKey: getGetBackupsQueryKey() })
              }
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-sm text-muted-foreground disabled:opacity-50"
            >
              <RefreshCw className={cn('w-4 h-4', query.isFetching && 'animate-spin')} />{' '}
              <T>Obnoviť</T>
            </button>
            <button
              type="button"
              data-trigger
              disabled={!writable || trigger.isPending}
              onClick={() => trigger.mutate()}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-xl',
                BUTTON_PRIMARY,
                'text-sm',
              )}
            >
              {trigger.isPending ? <Spinner /> : <Database className="w-4 h-4" />}{' '}
              <T>Zálohovať teraz</T>
            </button>
          </div>
        }
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {/* FB-151. Where the files are, and what the 24 counts. */}
      <p className="text-xs text-muted-foreground flex items-start gap-2" data-storage-note>
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        {storageNote()}
      </p>

      {query.isError ? (
        <div
          className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-xs space-y-2"
          data-load-failed
          data-backups-failed
        >
          <p className="font-bold text-red-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <T>Zálohy sa nepodarilo načítať</T>
          </p>
          <p className="text-muted-foreground">
            <T>Neznamená to, že žiadne nie sú - len sa nepodarilo zistiť, aké sú.</T>
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            data-backups-retry
            className="px-2.5 py-1 rounded-lg border border-border font-semibold text-foreground"
          >
            <T>Skúsiť znova</T>
          </button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-list-state={state}>
          {listMessage(state, backups.length)}
        </p>
      )}

      {/* FB-151 - the answer the page used to say was "in the system log somewhere". */}
      {/* **`runState` gains no fourth state, deliberately.** It grades the job into never / failed / ok, and
          an absent payload lands on `never` - *nobody switched this on* - where the live answer on the
          owner's data is `failed` with *Object storage not configured*. A `'unknown'` would put the request's
          failure inside a rule about the job, so the page withholds the graded sentence instead. */}
      {/* §9.533, owner #80 - **the read succeeded and its answer is still misleading.** Production pins
          `AGOS_JOBS_ENABLED=false`, so the hourly job *cannot* run and *never ran* is true but reads as
          *broken*. Said beside the graded sentence rather than replacing it, because it is a different fact:
          one is about the job's history, this is about the installation. */}
      {jobsOff !== null && (
        <p className="text-xs text-muted-foreground flex items-start gap-2" data-jobs-off>
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <T>{jobsOff}</T>
        </p>
      )}

      {lastRunQuery.isError ? (
        <p className="text-xs text-amber-500 flex items-start gap-2" data-last-run-unreachable>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <T>Stav poslednej zálohovacej úlohy sa nepodarilo načítať.</T>
        </p>
      ) : (
        !lastRunQuery.isLoading && (
          <p
            className={cn(
              'text-xs flex items-start gap-2',
              ran === 'ok' ? 'text-muted-foreground' : 'text-amber-500',
            )}
            data-last-run={ran}
          >
            {ran === 'ok' ? (
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            )}
            {lastRunMessage(lastRun)}
          </p>
        )
      )}

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {query.isError ? null : query.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : backups.length === 0 ? (
          <EmptyState
            icon={<Database className="w-10 h-10" />}
            title={tt('Žiadna záloha na disku')}
            /* The list really is empty here. The description still borrows the graded sentence, so it needs
               the same guard: a genuinely empty list beside a failed last-run read must not explain itself
               with *never ran*. */
            description={
              lastRunQuery.isError
                ? tt('Stav poslednej zálohovacej úlohy sa nepodarilo načítať.')
                : lastRunQuery.isLoading
                  ? listMessage('none-yet', 0)
                  : lastRunMessage(lastRun)
            }
          />
        ) : (
          <>
            <div className="px-5 py-3 border-b border-border flex items-center gap-3 text-xs text-muted-foreground">
              <span data-count>
                {formatCount(backups.length)} <T>záloh</T>
              </span>
              <span data-total-size>{formatSize(totalSize(backups))}</span>
            </div>
            <div className="divide-y divide-border">
              {backups.map((backup) => (
                <div
                  key={backup.filename}
                  className="flex items-center justify-between px-5 py-3.5 gap-4"
                  data-backup={backup.filename}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Database className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-mono truncate">{backup.filename}</p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {backup.createdAt === '' ? '-' : formatDateTime(backup.createdAt)}
                        </span>
                        <span className="flex items-center gap-1" data-size>
                          <HardDrive className="w-3 h-3" />
                          {formatSize(backup.sizeBytes)}
                        </span>
                        {databaseOf(backup.filename) !== null && (
                          <span data-database>{databaseOf(backup.filename)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    data-download={backup.filename}
                    disabled={
                      downloading === backup.filename || !isServableFilename(backup.filename)
                    }
                    onClick={() => void download(backup)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-background text-xs text-muted-foreground disabled:opacity-50 shrink-0"
                  >
                    {downloading === backup.filename ? (
                      <Spinner />
                    ) : (
                      <Download className="w-3 h-3" />
                    )}{' '}
                    <T>Stiahnuť</T>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div
        className="rounded-2xl border border-amber-500/20 bg-amber-500/5 px-5 py-4 space-y-1"
        data-restore
      >
        <p className="text-sm font-semibold text-amber-500">
          <T>Ako obnoviť zálohu</T>
        </p>
        <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
          {restoreInstructions().map((step) => (
            <li
              key={step}
              className={cn(
                step.startsWith('gunzip') || step.startsWith('psql') ? 'font-mono' : '',
              )}
            >
              {step}
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}
